import { useCallback, useEffect, useMemo, useState } from 'react';
import StatusBanner, { StatusBannerClosable, StatusBannerProps } from './status-banner';
import useIsClient from '@/util-react-router/hooks/use-is-client';

export interface StatusBannerDismissibleProps
  extends Omit<StatusBannerProps, 'closable'> {
  /** Should banner be closable for the customer? */
  closable?: {
    storageKey: string;
    useLocalStorage?: boolean;
  };
  className?: string;
}

/**
 * Status banner that can be configured via closable object to hide/show based on session or local storage key
 */
export default function StatusBannerDismissible({
  variant,
  title,
  message,
  primaryLinkButton: primaryButton,
  secondaryLinkButton: secondaryButton,
  icon,
  shouldScrollToTop,
  closable,
  compact,
  className,
}: StatusBannerDismissibleProps) {
  const [isClosed, setIsClosed] = useState<boolean>(true);
  const isClient = useIsClient();

  const closeBanner = useCallback(() => {
    if (closable?.storageKey) {
      if (closable?.useLocalStorage) {
        localStorage?.setItem(closable?.storageKey, 'true');
      } else {
        sessionStorage?.setItem(closable?.storageKey, 'true');
      }
      setIsClosed(true);
    }
  }, [closable?.storageKey, closable?.useLocalStorage]);

  const closableConfiguration: StatusBannerClosable = useMemo(() => {
    return { isClosed: isClosed, onClickDelegate: closeBanner };
  }, [closeBanner, isClosed]);

  useEffect(() => {
    if (closable?.storageKey && isClient) {
      if (closable?.useLocalStorage) {
        setIsClosed(localStorage?.getItem(closable.storageKey) === 'true');
      } else {
        setIsClosed(sessionStorage?.getItem(closable.storageKey) === 'true');
      }
    }
  }, [closable?.storageKey, closable?.useLocalStorage, isClient]);

  return (
    <StatusBanner
      variant={variant}
      title={title}
      message={message}
      primaryLinkButton={primaryButton}
      secondaryLinkButton={secondaryButton}
      icon={icon}
      shouldScrollToTop={shouldScrollToTop}
      closableConfiguration={closable ? closableConfiguration : undefined}
      compact={compact}
      className={className}
    />
  );
}
